// src/utils/shiftGuard.js
// SHIFT GUARD VERIFIER: shiftguard-4d2a8f61
//
// Shared pure logic for the forgotten-clock-out / stale-shift guards. NO UI,
// no React. The only imports are Firestore field-value helpers (pure sentinel
// factories), identical modular-v9 in both repos.
//
// KEEP IN SYNC with the mobile repo's src/utils/shiftGuard.js. This file is
// meant to be BYTE-IDENTICAL across both repos — not a subset. A reviewer
// should be able to `diff` the two files and see zero divergence. If you change
// a constant or a function here, mirror it there in the same commit.
//
// EXPORTS (diff-at-a-glance list):
//   STALE_MS
//   SELF_REPORT_EDIT_REASON, SELF_REPORT_SOURCE
//   CLOCK_OUT_STALE_TITLE, CLOCK_OUT_STALE_BODY
//   CLOCK_IN_STALE_TITLE,  CLOCK_IN_STALE_BODY
//   FINISHED_NOW_LABEL, SUBMIT_LABEL, CANCEL_LABEL
//   ERROR_COPY  (keyed by errorKey)
//   classifyOpenShift(newestOpenShift, now, hasPendingWrites)
//   classifyClockOut(shiftStart, now)
//   validateSelfReportedEnd(shiftStart, entered, now, nextShiftStart)
//   buildSelfReportedEndUpdate(shiftStart, entered, uid)
//
// NOTE: there is deliberately no RECENT_MS. Clock-in adopts ANY open shift
// under STALE_MS (a live in-progress shift, not just a double-tap), so a
// separate "recent" threshold would have no consumer.

import { serverTimestamp, Timestamp, arrayUnion } from 'firebase/firestore';

// ── Thresholds ──────────────────────────────────────────────────────────────
// 13h. Tradeoff, deliberately asymmetric: a FALSE prompt costs the tech one tap
// on "I finished just now"; a MISSED detection machine-stamps a corrupt payroll
// record — the exact harm this feature exists to prevent. So we bias low. 13h
// clears the longest legitimate shift seen in production (11.5h) by 1.5h, while
// still catching the likely real miss (finish 5pm, forget to punch out, return
// 7am next day = 14h) that a 16h line would have silently machine-stamped.
export const STALE_MS = 13 * 60 * 60 * 1000;

// ── Copy (shared so web and mobile read identically) ────────────────────────
export const SELF_REPORT_EDIT_REASON =
  'Self-reported clock-out — shift was left open and closed by the employee stating when they finished.';
export const SELF_REPORT_SOURCE = 'self-report';

export const CLOCK_OUT_STALE_TITLE = 'When did this shift end?';
export const CLOCK_OUT_STALE_BODY =
  "This shift's been open a long time. Pick the time you actually finished — we won't guess it for you.";

export const CLOCK_IN_STALE_TITLE = 'Finish your last shift first';
export const CLOCK_IN_STALE_BODY =
  "You've still got a shift open from earlier. Tell us when you actually finished it, then we'll start your new one.";

export const FINISHED_NOW_LABEL = 'I finished just now';
export const SUBMIT_LABEL = 'Save clock-out';
export const CANCEL_LABEL = 'Cancel';

export const ERROR_COPY = {
  END_REQUIRED: 'Choose the time you finished.',
  END_BEFORE_START: 'The end time has to be after you clocked in.',
  END_IN_FUTURE: "The end time can't be in the future.",
  END_AFTER_NEXT:
    'You already started another shift after this one — the end time has to be before that next shift began.',
};

// ── Instant coercion (CHANGE 3) ─────────────────────────────────────────────
// Callers pass shiftStart as a Firestore Timestamp, `now`/`entered` as millis
// (or a Date), and nextShiftStart as a Timestamp-or-null. A raw number compared
// against a Timestamp with `<=` does NOT throw — it silently evaluates false, so
// every validation rule would pass and the whole check would become a no-op.
// Every instant argument is funnelled through this first. Returns millis|null.
function toMillis(x) {
  if (x == null) return null;
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  if (typeof x.toMillis === 'function') return x.toMillis();      // Firestore Timestamp
  if (x instanceof Date) return x.getTime();                       // JS Date
  if (typeof x.seconds === 'number') {                             // plain {seconds,nanoseconds}
    return x.seconds * 1000 + (x.nanoseconds || 0) / 1e6;
  }
  return null;
}

// ── Clock-in classification ─────────────────────────────────────────────────
// `hasPendingWrites` is the newest open shift's snapshot-metadata flag. A pure
// function can't tell a not-yet-resolved local serverTimestamp (benign: adopt)
// from a genuinely corrupt server doc that has lost its shiftStart (must be
// human-resolved: promptStale). The caller supplies that fact from the getDocs
// snapshot metadata. Default is the CONSERVATIVE branch (promptStale) so a
// missing flag never silently adopts a corrupt doc.
export function classifyOpenShift(newestOpenShift, now, hasPendingWrites = false) {
  if (!newestOpenShift) return 'none';
  const startMs = toMillis(newestOpenShift.shiftStart);
  const nowMs = toMillis(now);
  if (startMs === null) {
    // No usable shiftStart. Adopt ONLY if it's a locally-pending write whose
    // serverTimestamp hasn't resolved yet; otherwise a human states the time.
    return hasPendingWrites ? 'adopt' : 'promptStale';
  }
  if (nowMs === null) return 'promptStale'; // can't reason about age → be safe
  const age = nowMs - startMs;
  return age < STALE_MS ? 'adopt' : 'promptStale';
}

// ── Clock-out classification ────────────────────────────────────────────────
export function classifyClockOut(shiftStart, now) {
  const startMs = toMillis(shiftStart);
  const nowMs = toMillis(now);
  // A missing/unreadable start on an OPEN shift we're closing is itself suspect
  // — prompt so a human states the end rather than machine-stamping blind.
  if (startMs === null || nowMs === null) return 'promptStale';
  const age = nowMs - startMs;
  return age < STALE_MS ? 'normal' : 'promptStale';
}

// ── Validation (instants only, no date math) ────────────────────────────────
// Returns an errorKey (into ERROR_COPY) or null when the entered end is valid.
export function validateSelfReportedEnd(shiftStart, entered, now, nextShiftStart) {
  const startMs = toMillis(shiftStart);
  const enteredMs = toMillis(entered);
  const nowMs = toMillis(now);
  const nextMs = toMillis(nextShiftStart);

  if (enteredMs === null) return 'END_REQUIRED';
  // (a) after the clock-in. Skipped only when the doc has no usable start
  //     (corrupt shift) — the other two rules still bound the entered time.
  if (startMs !== null && enteredMs <= startMs) return 'END_BEFORE_START';
  // (b) not in the future.
  if (nowMs !== null && enteredMs > nowMs) return 'END_IN_FUTURE';
  // (c) before the user's NEXT shift, if one exists — so an orphaned Monday
  //     shift surfacing on Friday can't be stamped through Tue/Wed/Thu.
  if (nextMs !== null && enteredMs > nextMs) return 'END_AFTER_NEXT';
  return null;
}

// ── The write (EXACTLY the six whitelisted fields — no seventh key) ──────────
// firestore.rules techShiftCloseSafe() allows a tech to touch only:
//   shiftEnd, shiftEndSelfReported, endedBy, endedAt, editReason, editHistory
// and only while the stored shiftEnd is null. This returns exactly that set.
// serverTimestamp() is illegal inside array elements, so the editHistory row
// stamps its own time with Timestamp.now().
export function buildSelfReportedEndUpdate(shiftStart, entered, uid) {
  const enteredMs = toMillis(entered);
  const endTs = Timestamp.fromMillis(enteredMs);
  return {
    shiftEnd: endTs,
    shiftEndSelfReported: true,
    endedBy: uid,
    endedAt: serverTimestamp(),
    editReason: SELF_REPORT_EDIT_REASON,
    editHistory: arrayUnion({
      previousEnd: null,
      newEnd: endTs,
      endedBy: uid,
      editedAt: Timestamp.now(),
      reason: SELF_REPORT_EDIT_REASON,
      source: SELF_REPORT_SOURCE,
    }),
  };
}
